"use client";

import { useEffect } from "react";

export interface UseHashScrollOptions {
  /**
   * Maximum number of retry attempts to find the target element
   * @default 15
   */
  maxRetries?: number;

  /**
   * Initial delay before starting to look for the element (in ms)
   * @default 300
   */
  initialDelay?: number;

  /**
   * Base delay for exponential backoff (in ms)
   * @default 200
   */
  baseDelay?: number;

  /**
   * Maximum delay between retries (in ms)
   * @default 1000
   */
  maxDelay?: number;

  /**
   * Exponential backoff multiplier
   * @default 1.2
   */
  backoffMultiplier?: number;

  /**
   * Custom element ID suffix (e.g., "__section")
   * @default "__section"
   */
  elementSuffix?: string;

  /**
   * Scroll behavior
   * @default "smooth"
   */
  scrollBehavior?: ScrollBehavior;

  /**
   * Scroll block position
   * @default "start"
   */
  scrollBlock?: ScrollLogicalPosition;
}

/**
 * Custom hook for handling hash-based scrolling to elements on the page.
 * Automatically handles lazy-loaded components and provides retry logic.
 *
 * @param options Configuration options for the hash scrolling behavior
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useHashScroll({
 *     maxRetries: 10,
 *     elementSuffix: "__section"
 *   });
 *
 *   return (
 *     <div>
 *       <div id="my-target__section">Target content</div>
 *     </div>
 *   );
 * }
 * ```
 */
export function useHashScroll(options: UseHashScrollOptions = {}) {
  const {
    maxRetries = 15,
    initialDelay = 300,
    baseDelay = 200,
    maxDelay = 1000,
    backoffMultiplier = 1.2,
    elementSuffix = "__section",
    scrollBehavior = "smooth",
    scrollBlock = "start",
  } = options;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleHashScroll = () => {
      const hash = window.location.hash;

      // Only proceed if we have a valid hash
      if (!hash || hash.length <= 1) {
        return;
      }

      const targetId = hash.substring(1); // Remove the # symbol
      const elementId = `${targetId}${elementSuffix}`;

      let retryCount = 0;

      const scrollToElement = () => {
        const targetElement = document.getElementById(elementId);
        if (targetElement) {
          // Element found, scroll to it
          targetElement.scrollIntoView({
            behavior: scrollBehavior,
            block: scrollBlock,
          });
        } else {
          retryCount++;

          if (retryCount < maxRetries) {
            // Exponential backoff: start with baseDelay, increase gradually
            const delay = Math.min(
              baseDelay * backoffMultiplier ** retryCount,
              maxDelay,
            );

            timeoutId = setTimeout(scrollToElement, delay);
          }
        }
      };

      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Initial delay to ensure components start rendering
      timeoutId = setTimeout(scrollToElement, initialDelay);
    };

    // Handle initial hash on page load
    handleHashScroll();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashScroll);

    // Cleanup function to prevent memory leaks
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener("hashchange", handleHashScroll);
    };
  }, [
    maxRetries,
    initialDelay,
    baseDelay,
    maxDelay,
    backoffMultiplier,
    elementSuffix,
    scrollBehavior,
    scrollBlock,
  ]);
}
